/**
 * « Par quoi commencer » : l'ordre dans lequel prendre les vingt-six outils.
 *
 * IL NE POSE QUE DES QUESTIONS QUI CHANGENT LE PLAN. La première version en
 * posait une cinquième, sur la catégorie d'activité : elle ne retirait ni
 * n'ajoutait aucune étape, parce que les plafonds, les taux et les seuils dont
 * elle décide sont l'affaire des outils vers lesquels le plan renvoie, et que
 * chacun la redemande. Le harnais l'a refusée, et il refusera la suivante.
 *
 * CE PARCOURS NE CALCULE RIEN ET NE PRODUIT AUCUN DOCUMENT. Il répond à la
 * seule question que la rubrique laissait sans réponse : par quoi commencer.
 * Vingt-six outils rangés par thème disent ce qu'on peut faire, jamais dans
 * quel ordre le faire, et quelqu'un qui n'a pas encore d'entreprise ne sait pas
 * qu'il doit demander l'ACRE dans les soixante jours ni déposer une déclaration
 * de CFE avant le 31 décembre. Ce sont les deux échéances les plus chères de la
 * première année, et rien ne les annonçait.
 *
 * IL ÉCARTE AUTANT QU'IL PROPOSE. Un plan de dix-huit étapes servi à tout le
 * monde ne vaut pas mieux qu'une liste alphabétique : les étapes qui ne
 * concernent pas le lecteur sortent du plan, et le parcours dit lesquelles et
 * pourquoi. Vendre à des professionnels seulement dispense du médiateur de la
 * consommation ; ne vendre aucun produit physique dispense de la REP.
 *
 * AUCUNE ÉCHÉANCE N'EST INVENTÉE ICI. Chacune pointe la constante du module qui
 * la porte déjà, pour qu'un changement de règle se propage au lieu de se
 * dédoubler. Le parcours ne demande pas la date de début d'activité : il énonce
 * donc la RÈGLE du délai, jamais un nombre de jours restants, qu'il serait
 * incapable de calculer honnêtement.
 */
import { ACRE_DELAI_DEMANDE_JOURS } from "./incomeTax.js";
import { MILLESIME } from "./cfeInitialReturn.js";
import { CALENDRIER } from "./electronicInvoicing.js";

/** Les trois moments d'un lancement, dans l'ordre. */
export const PHASES = ["avant", "creation", "apres"];

/** Les réponses que le parcours pose, et leurs valeurs admises. */
export const QUESTIONS = {
    avancement: ["idee", "declare", "immatricule"],
    clients: ["particuliers", "professionnels", "deux"],
    produits: ["aucun", "sansExpedition", "avecExpedition"],
    local: ["chezMoi", "chezUnProche", "localPro"],
};

/** Le lecteur vend-il, au moins en partie, à des particuliers ? */
function vendAuxParticuliers(r) {
    return r.clients === "particuliers" || r.clients === "deux";
}

/**
 * Les étapes du lancement, dans l'ordre où elles se font.
 *
 * `outil` est la clé du registre : c'est lui qui fournit le slug et le nom, de
 * sorte qu'un outil renommé n'a pas à l'être ici. `quand` décide si l'étape
 * entre au plan ; son absence signifie « toujours ». `ecarteeSi` donne le motif
 * du retrait, parce qu'une étape retirée sans raison ressemble à un oubli.
 */
export const ETAPES = [
    /* --- Avant de créer ---------------------------------------------------- */
    {
        cle: "regime",
        phase: "avant",
        outil: "plafonds",
    },
    {
        cle: "revenu",
        phase: "avant",
        outil: "revenu-net",
    },
    {
        cle: "acreDroit",
        phase: "avant",
        outil: "acre",
    },
    {
        cle: "adresse",
        phase: "avant",
        outil: "domiciliation",
        quand: (r) => r.local !== "localPro",
        ecarteeSi: "localPro",
    },
    {
        cle: "ape",
        phase: "avant",
        outil: "revision-code-ape",
    },

    /* --- La création ------------------------------------------------------- */
    {
        cle: "guichet",
        phase: "creation",
        outil: null,
        // La seule étape que la rubrique ne fait pas à la place du lecteur :
        // la déclaration se dépose sur le guichet unique, et nulle part ailleurs.
        lien: "https://formalites.entreprise.gouv.fr",
    },
    {
        cle: "nonCondamnation",
        phase: "creation",
        outil: "non-condamnation",
    },
    {
        cle: "conjoint",
        phase: "creation",
        outil: "information-conjoint",
    },

    /* --- Après la création ------------------------------------------------- */
    {
        cle: "demandeAcre",
        phase: "apres",
        outil: "demande-acre",
        echeance: "acre",
    },
    {
        cle: "cfe",
        phase: "apres",
        outil: "cfe-declaration-initiale",
        echeance: "cfe",
    },
    {
        cle: "assurances",
        phase: "apres",
        outil: "assurances-obligatoires",
    },
    {
        cle: "mediateur",
        phase: "apres",
        outil: "mediateur-consommation",
        quand: vendAuxParticuliers,
        ecarteeSi: "professionnelsSeulement",
    },
    {
        cle: "rep",
        phase: "apres",
        outil: "rep-emballages",
        quand: (r) => r.produits !== "aucun" && vendAuxParticuliers(r),
        ecarteeSi: "aucunProduit",
    },
    {
        cle: "devis",
        phase: "apres",
        outil: "devis",
    },
    {
        cle: "facture",
        phase: "apres",
        outil: "facturation-electronique",
        echeance: "facturation",
    },
    {
        cle: "tva",
        phase: "apres",
        outil: "seuils-tva",
    },
    {
        cle: "vfl",
        phase: "apres",
        outil: "eligibilite-versement-liberatoire",
        echeance: "vfl",
    },
    {
        cle: "retraite",
        phase: "apres",
        outil: "trimestres-retraite",
    },
];

/**
 * Les quatre échéances du plan, chacune reprise du module qui la porte.
 *
 * Ce sont des RÈGLES, pas des dates calculées : le parcours ne demande pas la
 * date de début d'activité, il ne peut donc pas dire combien de jours restent,
 * et prétendre le contraire serait faux pour la plupart des lecteurs.
 */
export const ECHEANCES = {
    acre: { jours: ACRE_DELAI_DEMANDE_JOURS },
    // Le 31 décembre de l'ANNÉE DE CRÉATION du lecteur, qu'on ne lui demande
    // pas : écrire le millésime de l'imprimé en cours donnerait une date fausse
    // à qui crée l'année suivante.
    cfe: { jour: "31 décembre", cerfa: MILLESIME.cerfa },
    vfl: { jour: "30 septembre" },
    facturation: { depuis: CALENDRIER.reception, emission: CALENDRIER.emission.micro },
};

/** Ce qui manque avant de rendre un plan. */
export function valider(reponses) {
    const r = reponses || {};
    const manques = Object.keys(QUESTIONS).filter(
        (q) => !QUESTIONS[q].includes(r[q]),
    );
    return { valide: manques.length === 0, manques, invalides: [], alertes: [] };
}

/**
 * Le plan personnalisé : ce qu'il y a à faire, dans l'ordre, et ce qui saute.
 *
 * Rend `null` tant qu'une réponse manque. Un plan partiel donnerait l'illusion
 * d'une liste complète alors qu'il lui manquerait précisément les étapes que la
 * réponse absente aurait ajoutées.
 */
export function plan(reponses) {
    const r = reponses || {};
    if (!valider(r).valide) return null;

    const retenues = [];
    const ecartees = [];
    for (const etape of ETAPES) {
        if (etape.quand && !etape.quand(r)) {
            ecartees.push({ cle: etape.cle, motif: etape.ecarteeSi });
            continue;
        }
        retenues.push(etape);
    }

    // Le lecteur déjà immatriculé a les étapes d'avant derrière lui : elles
    // restent listées, mais à part, parce qu'une étape franchie n'est pas une
    // étape à faire et que les mêler ferait un plan faux de cinq lignes.
    const franchies =
        r.avancement === "immatricule"
            ? retenues.filter((e) => e.phase !== "apres")
            : r.avancement === "declare"
              ? retenues.filter((e) => e.phase === "avant")
              : [];
    const franchiesCles = new Set(franchies.map((e) => e.cle));
    const aFaire = retenues.filter((e) => !franchiesCles.has(e.cle));

    const parPhase = PHASES.map((phase) => ({
        phase,
        etapes: aFaire.filter((e) => e.phase === phase),
    })).filter((p) => p.etapes.length > 0);

    return {
        phases: parPhase,
        aFaire,
        franchies,
        ecartees,
        total: aFaire.length,
        // La première étape à faire : c'est la seule chose qu'on retient d'un
        // plan de quinze lignes, et elle mérite d'être nommée à part.
        premiere: aFaire[0] || null,
    };
}
